/**
 * ProtectedRoute — guards all admin routes behind authentication.
 *
 * - While auth state is loading (token refresh on mount): shows full-screen spinner
 * - If not authenticated: redirects to /login
 * - If authenticated but role not in allowedRoles: renders <Unauthorized />
 * - Otherwise: renders child routes via <Outlet />
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth, type AdminRole } from "@/hooks/use-auth";
import { LoadingSpinner } from "@/components/shared/loading-spinner";
import {
  ShieldOff,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProtectedRouteProps {
  /** Roles allowed through this gate. Defaults to all admin roles. */
  allowedRoles?: AdminRole[];
}

function FullPageSpinner() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-muted-foreground animate-pulse">
          Verifying session…
        </p>
      </div>
    </div>
  );
}

function Unauthorized() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6 text-center max-w-sm px-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
          <ShieldOff className="h-8 w-8 text-destructive" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold">Access Denied</h1>
          <p className="text-sm text-muted-foreground">
            You don't have the required role to access this page. Contact your
            system administrator.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.history.back()}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Go back
        </Button>
      </div>
    </div>
  );
}

const ALL_ADMIN_ROLES: AdminRole[] = [
  "super_admin",
  "org_admin",
  "compliance_officer",
];

export function ProtectedRoute({
  allowedRoles = ALL_ADMIN_ROLES,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user && !allowedRoles.includes(user.role as AdminRole)) {
    return <Unauthorized />;
  }

  return <Outlet />;
}
